import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import MainLayout from '../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../components/common/TimeRangeSelector';
import apmErrorsService, { type ErrorStats, type ErrorGroup, type ErrorTrace } from '../../services/api/apm-errors.service';
import {
  ExclamationTriangleIcon,
  ChartBarIcon,
  ClockIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';

export default function APMErrorsPage() {
  const { t } = useTranslation('apm-errors');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();

  // Get selected application from Redux store
  const selectedAppId = useAppSelector((state: any) => state.application?.selectedApplicationId) || 'all';

  const [stats, setStats] = useState<ErrorStats>({
    totalErrors: 0,
    errorTypes: 0,
    timeline: [],
    topErrors: [],
  });
  const [errors, setErrors] = useState<ErrorGroup[]>([]);
  const [selectedError, setSelectedError] = useState<ErrorGroup | null>(null);
  const [errorTraces, setErrorTraces] = useState<ErrorTrace[]>([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalErrors, setTotalErrors] = useState(0);
  const itemsPerPage = 20;

  // Time range
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '24h',
  });

  useEffect(() => {
    if (selectedAppId) {
      loadData();
    }
  }, [selectedAppId, timeRange, currentPage]);

  const loadData = async () => {
    if (!selectedAppId) return;

    setLoading(true);
    try {
      const [statsResponse, errorsResponse] = await Promise.all([
        apmErrorsService.getStats(selectedAppId, timeRange.startTime, timeRange.endTime),
        apmErrorsService.getErrors(selectedAppId, timeRange.startTime, timeRange.endTime, {
          limit: itemsPerPage,
          offset: (currentPage - 1) * itemsPerPage,
        }),
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      if (errorsResponse.success) {
        setErrors(errorsResponse.data);
        setTotalErrors(errorsResponse.meta.total);
      }
    } catch (error) {
      console.error('Error loading APM errors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleErrorClick = async (error: ErrorGroup) => {
    setSelectedError(error);
    setShowDetailsModal(true);

    try {
      const response = await apmErrorsService.getErrorTraces(
        selectedAppId,
        error.errorType,
        timeRange.startTime,
        timeRange.endTime,
        error.operationName
      );

      if (response.success) {
        setErrorTraces(response.data);
      }
    } catch (error) {
      console.error('Error loading error traces:', error);
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const totalPages = Math.ceil(totalErrors / itemsPerPage);

  return (
    <MainLayout>
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('title')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('subtitle')}
          </p>
        </div>

        {/* Time Range Filter */}
        <div className="mb-6">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('stats.totalErrors')}
                </p>
                <p className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">
                  {stats.totalErrors.toLocaleString()}
                </p>
              </div>
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('stats.errorTypes')}
                </p>
                <p className="mt-2 text-3xl font-bold text-orange-600 dark:text-orange-400">
                  {stats.errorTypes}
                </p>
              </div>
              <ChartBarIcon className="h-12 w-12 text-orange-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('stats.avgDuration')}
                </p>
                <p className="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {errors.length > 0 ? formatDuration(errors.reduce((sum, e) => sum + e.avgDuration, 0) / errors.length) : '-'}
                </p>
              </div>
              <ClockIcon className="h-12 w-12 text-blue-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {t('stats.lastError')}
                </p>
                <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                  {errors.length > 0 ? new Date(errors[0].lastSeen).toLocaleString() : '-'}
                </p>
              </div>
              <CodeBracketIcon className="h-12 w-12 text-purple-500" />
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Timeline Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('timeline.title')}
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={stats.timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis
                  dataKey="time"
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF' }}
                  tickFormatter={(value) => new Date(value).toLocaleTimeString()}
                />
                <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleString()}
                />
                <Line type="monotone" dataKey="errors" stroke="#EF4444" strokeWidth={2} name={t('timeline.errors')} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Top Errors Chart */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('topErrors.title')}
            </h2>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.topErrors} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF' }} />
                <YAxis
                  type="category"
                  dataKey="type"
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF' }}
                  width={150}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                />
                <Bar dataKey="count" fill="#F59E0B" name={t('topErrors.count')} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Error List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('errorList.title')}
            </h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : errors.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              {t('errorList.noErrors')}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('errorList.columns.errorType')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('errorList.columns.message')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('errorList.columns.operation')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('errorList.columns.occurrences')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('errorList.columns.lastSeen')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {t('errorList.columns.avgDuration')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {errors.map((error, index) => (
                      <tr
                        key={index}
                        onClick={() => handleErrorClick(error)}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            {error.errorType}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 dark:text-white truncate max-w-md">
                            {error.errorMessage}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {error.operationName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {error.occurrences}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {new Date(error.lastSeen).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {formatDuration(error.avgDuration)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination - Always show with item count */}
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {((currentPage - 1) * itemsPerPage) + 1}
                  </span>
                  {' - '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {Math.min(currentPage * itemsPerPage, totalErrors)}
                  </span>
                  {' de '}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {totalErrors}
                  </span>
                  {' erros'}
                  {totalPages > 1 && (
                    <span className="ml-2">
                      (Página {currentPage} de {totalPages})
                    </span>
                  )}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Primeira página"
                    >
                      «
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {tCommon('previous')}
                    </button>
                    <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {tCommon('next')}
                    </button>
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Última página"
                    >
                      »
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Error Details Modal */}
      <Transition appear show={showDetailsModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDetailsModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
                    {t('errorDetails.title')}
                  </Dialog.Title>

                  {selectedError && (
                    <div className="space-y-4">
                      {/* Error Info */}
                      <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              {t('errorDetails.errorType')}
                            </p>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                              {selectedError.errorType}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              {t('errorList.columns.occurrences')}
                            </p>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedError.occurrences}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                            {t('errorDetails.errorMessage')}
                          </p>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedError.errorMessage}
                          </p>
                        </div>
                      </div>

                      {/* Error Traces */}
                      <div>
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {t('errorDetails.traces')}
                        </h4>
                        <div className="space-y-2 max-h-96 overflow-y-auto">
                          {errorTraces.map((trace, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {trace.operationName}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {new Date(trace.timestamp).toLocaleString()}
                                  </p>
                                </div>
                                <button
                                  onClick={() => navigate(`/apm/traces/${selectedAppId}/${trace.traceId}`)}
                                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                                >
                                  {t('errorDetails.viewTrace')}
                                </button>
                              </div>

                              {trace.errorStack && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                                    {t('errorDetails.stackTrace')}
                                  </p>
                                  <pre className="text-xs text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-950 p-2 rounded overflow-x-auto max-h-32">
                                    {trace.errorStack}
                                  </pre>
                                </div>
                              )}

                              {(trace.context.dbSystem || trace.context.httpUrl) && (
                                <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                                  {trace.context.dbSystem && (
                                    <p>
                                      <span className="font-medium">{t('errorDetails.database')}:</span>{' '}
                                      {trace.context.dbSystem} - {trace.context.dbStatement?.substring(0, 100)}...
                                    </p>
                                  )}
                                  {trace.context.httpUrl && (
                                    <p>
                                      <span className="font-medium">{t('errorDetails.http')}:</span>{' '}
                                      {trace.context.httpMethod} {trace.context.httpUrl} ({trace.context.httpStatusCode})
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                          onClick={() => setShowDetailsModal(false)}
                        >
                          {tCommon('close')}
                        </button>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </MainLayout>
  );
}
